import * as angular from "angular";
import { keyBy } from "lodash";
import swal from "sweetalert2";
import {
	FORM_ENTITY_IDENTIFIERS,
	FORM_ENTITY_IDENTIFIERS_UPDATE
} from "../../../../../../../routes";
import {
	getAllowedIdentifiers,
	getRequiredIdentifiersForType
} from "../../../../../getAllowedIdentifiers";
import { globs } from "../../../../../../../services/globs";
import {
	applyExpectedIdentifierRows,
	buildIdentifierRows,
	createIdentifierDraftRow,
	hasIdentifierText,
	isIdentifierDraftRowBlank,
	normalizeIdentifierValue,
	reconcileIdentifierDraftRows
} from "../identifierRows";
import { IdentifierEditPlan } from "./identifierEditPlan";
import template from "./entity-identifiers-editor.html";
import "./entity-identifiers-editor.less";

export function controller(appState, loadingService, api, $logger, securityService, toastr) {
	const ctrl = this;
	const newRowIdPrefix = "new-identifier-row";
	let nextNewRowId = 0;
	let identifierActions;

	ctrl.$onChanges = onChanges;
	ctrl.beginEditing = beginEditing;
	ctrl.leaveEditing = leaveEditing;
	ctrl.saveChanges = saveChanges;
	ctrl.save = saveChanges;
	ctrl.isDirty = isDirty;
	ctrl.hasEditableRows = hasEditableRows;
	ctrl.toggleRowActive = toggleRowActive;
	ctrl.canEditRowValue = canEditRowValue;
	ctrl.canToggleRowActive = canToggleRowActive;
	ctrl.isPendingInactiveRow = isPendingInactiveRow;
	ctrl.isRowVisible = isRowVisible;
	ctrl.isExpectedMissingRow = isExpectedMissingRow;
	ctrl.getIdentifierTypeName = getIdentifierTypeName;
	ctrl.getIdentifierToggleLabel = getIdentifierToggleLabel;
	ctrl.hasInactiveRows = hasInactiveRows;
	ctrl.handleRowTypeChanged = handleRowTypeChanged;
	ctrl.handleRowValueChanged = handleRowValueChanged;

	ctrl.$onInit = () => {
		ctrl.isLoading = false;
		identifierActions = globs.entityTypes
			.byType(ctrl.typeConfig.Code)
			.identifiers;
		initializeTypeMetadata();
		ctrl.currentIdentifiers = [];
		ctrl.stagedRows = [];
		syncState();
	};

	function onChanges(changes) {
		const identifiersChange = changes.identifiers;
		if(!identifiersChange || identifiersChange.isFirstChange()) {
			return;
		}

		syncState(identifiersChange.currentValue);
	}

	function initializeTypeMetadata() {
		const agencyIdentifierTypes = appState.forms.selected.Agency.IdentifierTypes ?? [];
		ctrl.identifierTypesByCode = keyBy(agencyIdentifierTypes, type => type.Code);

		ctrl.creatableIdentifierTypes = getAllowedIdentifiers(
			ctrl.typeConfig.Identifiers,
			agencyIdentifierTypes,
			identifier => isTypeCreatable(identifier.Item?.Code ?? identifier.Code)
		)
			.filter(Boolean);

		ctrl.expectedIdentifierTypes = getRequiredIdentifiersForType(
			ctrl.typeConfig.Identifiers,
			agencyIdentifierTypes
		)
			.filter(Boolean)
			.filter(type => isTypeReadable(type.Code));
	}

	function syncState(identifiers = ctrl.identifiers) {
		ctrl.currentIdentifiers = identifiers;
		ctrl.stagedRows = buildIdentifierRows(
			ctrl.currentIdentifiers,
			ctrl.expectedIdentifierTypes
		);
		ctrl.editPlan = new IdentifierEditPlan(
			ctrl.currentIdentifiers,
			ctrl.stagedRows,
			{
				canCreate: isTypeCreatable,
				canUpdate: isTypeUpdatable
			}
		);
		reconcileRows();
	}

	function beginEditing() {
		syncState();
		return hasEditableRows();
	}

	function leaveEditing() {
		syncState();
		return true;
	}

	function isDirty() {
		return ctrl.editPlan.isDirty
			|| ctrl.stagedRows.some(row => row.isCreateRow && !isIdentifierDraftRowBlank(row));
	}

	function hasEditableRows() {
		if(ctrl.readonly) {
			return false;
		}

		if(ctrl.creatableIdentifierTypes.length > 0) {
			return true;
		}

		return ctrl.currentIdentifiers.some(identifier => {
			const code = identifier.Type.Code;
			return isTypeUpdatable(code)
				|| (identifier.IsActive ? isTypeDeactivatable(code) : isTypeReactivatable(code));
		});
	}

	function hasInactiveRows() {
		return ctrl.stagedRows.some(row => !row.IsActive && !row.isExpectedMissing);
	}

	function isRowVisible(row) {
		if(isPendingInactiveRow(row)) {
			return true;
		}

		return row.IsActive || ctrl.includeInactive || row.isExpectedMissing;
	}

	function isExpectedMissingRow(row) {
		return !!row.isExpectedMissing;
	}

	function getIdentifierTypeName(code) {
		return ctrl.identifierTypesByCode[code]?.Name ?? code;
	}

	function getIdentifierToggleLabel(row) {
		if(row.isCreateRow) {
			const typeName = row.Type?.Code
				? getIdentifierTypeName(row.Type.Code)
				: "New";
			return `${typeName} identifier will be active`;
		}

		const action = row.IsActive ? "Deactivate" : "Reactivate";
		return `${action} ${getIdentifierTypeName(row.Type.Code)} identifier`;
	}

	function canEditRowValue(row) {
		if(ctrl.readonly || ctrl.isLoading || !row?.Type?.Code) {
			return false;
		}

		if(!row.IsActive) {
			return false;
		}

		return row.Id ? isTypeUpdatable(row.Type.Code) : isTypeCreatable(row.Type.Code);
	}

	function canToggleRowActive(row) {
		if(ctrl.readonly
			|| ctrl.isLoading
			|| !row?.Type?.Code
			|| row.isExpectedMissing
			|| row.isCreateRow) {
			return false;
		}

		if(row.Id) {
			return row.IsActive
				? isTypeDeactivatable(row.Type.Code)
				: isTypeReactivatable(row.Type.Code);
		}

		return isTypeCreatable(row.Type.Code);
	}

	function toggleRowActive(row) {
		if(!canToggleRowActive(row)) {
			return;
		}

		row.IsActive = !row.IsActive;
		reconcileRows();
	}

	function isPendingInactiveRow(row) {
		return !row.IsActive
			&& (row.OriginalIsActive || row.isNew)
			&& !row.isExpectedMissing;
	}

	function handleRowValueChanged(row) {
		if(row.isExpectedMissing && hasIdentifierText(row.Value)) {
			row.isExpectedMissing = false;
			row.isPlaceholderExpected = false;
		}

		reconcileRows(row.rowKey);
	}

	function handleRowTypeChanged(row) {
		reconcileRows(row.rowKey);
	}

	async function saveChanges() {
		if(ctrl.isLoading) {
			return false;
		}

		const validationErrors = validateStagedRows();
		if(validationErrors.length > 0) {
			await swal.fire({
				title: "Identifiers Need Attention",
				html: `<ul><li>${validationErrors.join("</li><li>")}</li></ul>`,
				showCloseButton: true,
				type: "warning"
			});
			return false;
		}

		const operationPlan = ctrl.editPlan.operationPlan;
		if(operationPlan.total === 0) {
			return true;
		}

		ctrl.isLoading = true;
		try {
			await loadingService.watch(applyIdentifierOperationPlan(operationPlan));
			appState.forms.isDirty = true;
			const identifiers = await reloadIdentifiers();
			syncState(identifiers);
			toastr.success(`${ctrl.entity.Name ?? ctrl.entity.Code} identifiers updated successfully.`);

			return true;
		}
		catch(err) {
			$logger.error("entity identifiers update error", err);
			const identifiers = await reloadIdentifiers();
			syncState(identifiers);

			await swal.fire({
				title: "Identifiers Reloaded",
				html:
					`<p>There was an error saving identifiers.</p>
					<p>The latest identifiers were reloaded from the server. Review the current values and try again.</p>`,
				showCloseButton: true,
				type: "warning"
			});

			return false;
		}
		finally {
			ctrl.isLoading = false;
		}
	}

	function reloadIdentifiers() {
		return ctrl.onReload();
	}

	function reconcileRows(changedRowKey) {
		const rowsWithoutMissing = ctrl.stagedRows.filter(row => !row.isExpectedMissing);
		const rowsWithExpected = applyExpectedIdentifierRows(
			rowsWithoutMissing,
			ctrl.expectedIdentifierTypes
		);
		const orderedRows = [
			...rowsWithExpected.filter(row => !row.isCreateRow),
			...rowsWithExpected.filter(row => row.isCreateRow)
		];
		const reconciledRows = ctrl.creatableIdentifierTypes.length > 0
			? reconcileIdentifierDraftRows(
				orderedRows,
				changedRowKey,
				createNewIdentifierRow
			)
			: orderedRows.filter(row => !row.isCreateRow);
		ctrl.stagedRows.splice(0, ctrl.stagedRows.length, ...reconciledRows);
	}

	function createNewIdentifierRow() {
		nextNewRowId += 1;
		return createIdentifierDraftRow(`${newRowIdPrefix}-${nextNewRowId}`);
	}

	function validateStagedRows() {
		const errors = [];

		for(const row of ctrl.stagedRows) {
			if(isIdentifierDraftRowBlank(row)) {
				continue;
			}

			if(!row.IsActive || row.isExpectedMissing) {
				continue;
			}

			const requiredFieldErrors = validateIdentifierRequiredFields(row.Type?.Code, row.Value);
			if(requiredFieldErrors.length > 0) {
				errors.push(...requiredFieldErrors);
				continue;
			}

			if(!isIdentifierPatternValid(row.Type.Code, row.Value)) {
				errors.push(`${getIdentifierTypeName(row.Type.Code)} is not a valid identifier format.`);
			}
		}

		for(const expectedType of ctrl.expectedIdentifierTypes) {
			const hasRequiredValue = ctrl.stagedRows.some(row =>
				row.Type?.Code === expectedType.Code
					&& row.IsActive
					&& hasIdentifierText(row.Value)
			);

			if(!hasRequiredValue) {
				errors.push(`${expectedType.Name} is required.`);
			}
		}

		return [...new Set(errors)];
	}

	function validateIdentifierRequiredFields(typeCode, value) {
		if(!hasIdentifierText(typeCode) && hasIdentifierText(value)) {
			return ["Identifier type is required."];
		}

		if(hasIdentifierText(typeCode) && !hasIdentifierText(value)) {
			return [`Identifier value is required for ${getIdentifierTypeName(typeCode)}.`];
		}

		return [];
	}

	async function applyIdentifierOperationPlan(plan) {
		for(const operation of plan.updates) {
			const route = FORM_ENTITY_IDENTIFIERS_UPDATE(
				appState.forms.selected.Id,
				ctrl.entity.Id,
				operation.id
			);
			await putIdentifier(route, {
				Id: operation.id,
				Type: {
					Code: operation.typeCode
				},
				Value: operation.value
			});
		}

		for(const operation of plan.stateChanges.filter(candidate => candidate.toActive)) {
			const route = FORM_ENTITY_IDENTIFIERS_UPDATE(
				appState.forms.selected.Id,
				ctrl.entity.Id,
				operation.id
			);
			await putIdentifier(route, {
				Id: operation.id,
				Type: {
					Code: operation.typeCode
				},
				Value: operation.value,
				IsActive: true
			});
		}

		for(const operation of plan.creates) {
			const route = FORM_ENTITY_IDENTIFIERS(appState.forms.selected.Id, ctrl.entity.Id);
			await postIdentifier(route, {
				Type: {
					Code: operation.typeCode
				},
				Value: operation.value
			});
		}

		for(const operation of plan.stateChanges.filter(candidate => !candidate.toActive)) {
			const route = FORM_ENTITY_IDENTIFIERS_UPDATE(
				appState.forms.selected.Id,
				ctrl.entity.Id,
				operation.id
			);
			await putIdentifier(route, {
				Id: operation.id,
				Type: {
					Code: operation.typeCode
				},
				Value: operation.value,
				IsActive: false
			});
		}
	}

	function putIdentifier(route, payload) {
		return ctrl.put
			? ctrl.put(route, payload)
			: api.put(route, payload);
	}

	function postIdentifier(route, payload) {
		return ctrl.post
			? ctrl.post(route, payload)
			: api.post(route, payload);
	}

	function isIdentifierPatternValid(typeCode, value) {
		const type = ctrl.identifierTypesByCode[typeCode];
		const normalizedValue = normalizeIdentifierValue(value);
		$logger.info("identifier value-pattern check", {
			typeCode,
			value: normalizedValue,
			valuePattern: type?.ValuePattern
		});

		if(!type?.ValuePattern) {
			return true;
		}

		try {
			const expression = new RegExp(type.ValuePattern);
			return expression.test(normalizedValue);
		}
		catch(err) {
			$logger.warn("identifier value-pattern regex parse failed", {
				typeCode,
				valuePattern: type?.ValuePattern,
				err
			});
			return true;
		}
	}

	function isTypeReadable(code) {
		return securityService.isActionAllowed(identifierActions.byCode(code).read);
	}

	function isTypeCreatable(code) {
		return securityService.isActionAllowed(identifierActions.byCode(code).create);
	}

	function isTypeUpdatable(code) {
		return securityService.isActionAllowed(identifierActions.byCode(code).update);
	}

	function isTypeDeactivatable(code) {
		return securityService.isActionAllowed(identifierActions.byCode(code).deactivate);
	}

	function isTypeReactivatable(code) {
		return securityService.isActionAllowed(identifierActions.byCode(code).reactivate);
	}
}

export default angular.module("vesl.components.entity-identifiers-editor", [])
	.component("entityIdentifiersEditor", {
		controller,
		template,
		bindings: {
			entity: "<",
			readonly: "<",
			typeConfig: "<",
			identifiers: "<",
			canShowInactiveToggle: "<",
			includeInactive: "<",
			onToggleInactive: "&",
			put: "=",
			post: "=",
			onReload: "&"
		}
	})
	.name;
